import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import Select from 'react-select';
import DispayAndEventsController, {
  getInitialDisplayAndEventsForm,
  initialDisplayAndEventsFilters
} from '../Controller/DispayAndEventsController';
import Loader from 'utils/Loader';
import { showAlert } from 'utils/ErrorMessage';
import { showSuccessAlert } from 'utils/SuccessMessage';

const textFieldProps = {
  InputLabelProps: {
    shrink: true
  },
  sx: {
    '& .MuiFormLabel-asterisk': {
      color: '#d32f2f'
    }
  }
};

const compactHeaderCellSx = {
  px: 1,
  py: 1,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  fontWeight: 700
};

const compactBodyCellSx = {
  px: 1,
  py: 0.75,
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  fontSize: '0.72rem',
  verticalAlign: 'top'
};

const centeredCellSx = { textAlign: 'center' };

const searchableDropdownStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 40,
    borderRadius: 4,
    borderColor: state.isFocused ? '#1976d2' : 'rgba(0, 0, 0, 0.23)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'rgba(0, 0, 0, 0.87)'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    minHeight: 40,
    padding: '0 14px'
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.6)'
  }),
  indicatorSeparator: () => ({
    display: 'none'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: 12
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 1300
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 1600
  })
};

const selectMenuPortalTarget = typeof document !== 'undefined' ? document.body : null;

const searchableFieldWrapperSx = {
  position: 'relative'
};

const searchableFieldLabelSx = {
  position: 'absolute',
  top: -8,
  left: 12,
  px: 0.5,
  backgroundColor: '#fff',
  color: 'rgba(0, 0, 0, 0.6)',
  fontSize: '0.75rem',
  lineHeight: 1,
  zIndex: 1
};

const getSearchableNoOptionsMessage = ({ inputValue }) => {
  if (String(inputValue || '').trim().length < 3) {
    return 'Type at least 3 characters';
  }

  return 'No data found';
};

const requiredFields = [
  'asmUserCode',
  'eventname',
  'registersrno',
  'division',
  'requestdate',
  'startdate',
  'enddate',
  'eventtype',
  'participatingareas',
  'expectedsalesqty',
  'expectedsalesamount',
  'expecteddealersno',
  'expectedretailersno',
  'customeravgdaystay',
  'expectedagentsno',
  'staffavgdaystay',
  'expectedstaffno',
  'hotelcostperperson',
  'travelcostperperson',
  'displaycost',
  'logisticscost',
  'photovideoshootcost'
];

const numericFields = [
  'expectedsalesqty',
  'expectedsalesamount',
  'expecteddealersno',
  'expectedretailersno',
  'customeravgdaystay',
  'expectedagentsno',
  'staffavgdaystay',
  'expectedstaffno',
  'hotelcostperperson',
  'hoteltotalcost',
  'travelcostperperson',
  'traveltotalcost',
  'displaycost',
  'logisticscost',
  'photovideoshootcost',
  'totalestimatedcost',
  'expenditurepercentage',
  'expenditurepercustomer'
];

const moneyFields = [
  'expectedsalesamount',
  'hotelcostperperson',
  'hoteltotalcost',
  'travelcostperperson',
  'traveltotalcost',
  'displaycost',
  'logisticscost',
  'photovideoshootcost',
  'totalestimatedcost',
  'expenditurepercustomer'
];

const dateFields = ['requestdate', 'startdate', 'enddate'];

const statusFilterOptions = [
  { value: 'Request Submitted', label: 'Request Submitted' },
  { value: 'ASM Approved', label: 'ASM Approved' },
  { value: 'ZSM Approved', label: 'ZSM Approved' },
  { value: 'Brand Head Approved', label: 'Brand Head Approved' },
  { value: 'ASM Rejected', label: 'ASM Rejected' },
  { value: 'ZSM Rejected', label: 'ZSM Rejected' },
  { value: 'Brand Head Rejected', label: 'Brand Head Rejected' }
];

const formFields = [
  { name: 'asmUserCode', label: 'Area Sales Manager', placeholder: 'Search ASM name or usercode' },
  { name: 'eventname', label: 'Event Name', placeholder: 'Retail Display Event Mumbai', md: 8 },
  { name: 'registersrno', label: 'Register Sr No', placeholder: 'REG002' },
  { name: 'division', label: 'Division', placeholder: 'Search Division' },
  { name: 'requestdate', label: 'Request Date', type: 'date' },
  { name: 'startdate', label: 'Start Date', type: 'date' },
  { name: 'enddate', label: 'End Date', type: 'date' },
  { name: 'eventtype', label: 'Event Type', placeholder: 'Retail Display' },
  { name: 'participatingareas', label: 'Participating Areas', placeholder: 'Mumbai,Thane,Navi Mumbai', md: 8 },
  { name: 'expectedsalesqty', label: 'Expected Sales Qty', placeholder: '1000' },
  { name: 'expectedsalesamount', label: 'Expected Sales Amount', placeholder: '5000000' },
  { name: 'expecteddealersno', label: 'Expected Dealers No', placeholder: '40' },
  { name: 'expectedretailersno', label: 'Expected Retailers No', placeholder: '100' },
  { name: 'customeravgdaystay', label: 'Customer Avg Day Stay', placeholder: '3' },
  { name: 'expectedagentsno', label: 'Expected Agents No', placeholder: '15' },
  { name: 'staffavgdaystay', label: 'Staff Avg Day Stay', placeholder: '3' },
  { name: 'expectedstaffno', label: 'Expected Staff No', placeholder: '10' },
  { name: 'hotelcostperperson', label: 'Hotel Cost Per Person', placeholder: '3000' },
  { name: 'hoteltotalcost', label: 'Hotel Total Cost', readOnly: true },
  { name: 'travelcostperperson', label: 'Travel Cost Per Person', placeholder: '1200' },
  { name: 'traveltotalcost', label: 'Travel Total Cost', readOnly: true },
  { name: 'displaycost', label: 'Display Cost', placeholder: '50000' },
  { name: 'logisticscost', label: 'Logistics Cost', placeholder: '25000' },
  { name: 'photovideoshootcost', label: 'Photo/Video Shoot Cost', placeholder: '15000' },
  { name: 'totalestimatedcost', label: 'Total Estimated Cost', readOnly: true },
  { name: 'expenditurepercentage', label: 'Expenditure Percentage', readOnly: true },
  { name: 'expenditurepercustomer', label: 'Expenditure Per Customer', readOnly: true }
];

const detailFields = [
  { name: 'eventid', label: 'Event ID' },
  ...formFields,
  { name: 'status', label: 'Status' },
];

const toNumber = (value) => {
  const parsedValue = Number(String(value || '').replace(/,/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const sanitizeNumericInput = (value) => {
  const cleanedValue = String(value || '').replace(/[^\d.]/g, '');
  const [wholePart, ...decimalParts] = cleanedValue.split('.');

  return decimalParts.length ? `${wholePart}.${decimalParts.join('')}` : wholePart;
};

const formatDecimal = (value, fractionDigits = 2) => {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return '';
  }

  return numericValue.toFixed(fractionDigits);
};

const formatDisplayDate = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '-';
  }

  const parsedDate = new Date(normalizedValue.includes(' ') ? normalizedValue.replace(' ', 'T') : `${normalizedValue}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const formatHistoryDateTime = (value) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '-';
  }

  const parsedDate = new Date(normalizedValue.includes(' ') ? normalizedValue.replace(' ', 'T') : normalizedValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return normalizedValue;
  }

  return parsedDate.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatAmount = (value) => {
  const numericValue = Number(value || 0);

  if (!Number.isFinite(numericValue)) {
    return value || '-';
  }

  return numericValue.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: String(value || '').includes('.') ? 2 : 0
  });
};

const getStatusTextSx = (status) => {
  const normalizedStatus = String(status || '').toLowerCase();

  if (normalizedStatus.includes('approved')) {
    return {
      color: '#1b5e20',
      fontWeight: 700
    };
  }

  if (normalizedStatus.includes('rejected')) {
    return {
      color: '#d2011d',
      fontWeight: 700
    };
  }

  return {
    color: '#b26a00',
    fontWeight: 700
  };
};

const DispayAndEvents = () => {
  const {
    actionHistory,
    asmUserOptions,
    clearActionHistory,
    divisionOptions,
    eventRequests,
    filters,
    historyError,
    isAsmUserOptionsLoading,
    isDivisionOptionsLoading,
    isHistoryLoading,
    isSubmitting,
    isTableLoading,
    loggedInUser,
    loadActionHistory,
    setAsmUserSearchText,
    setFilters,
    submitEventRequest,
    totalRows
  } = DispayAndEventsController();
  const initialForm = useMemo(() => getInitialDisplayAndEventsForm(loggedInUser), [loggedInUser]);
  const [formData, setFormData] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedEventRequest, setSelectedEventRequest] = useState(null);
  const [selectedActionHistoryEvent, setSelectedActionHistoryEvent] = useState(null);
  const [selectedAsmUserOption, setSelectedAsmUserOption] = useState(null);
  const [selectedDivisionOption, setSelectedDivisionOption] = useState(null);

  const resolvedAsmUserOption = useMemo(
    () => asmUserOptions.find((option) => option.value === formData.asmUserCode)
      || selectedAsmUserOption
      || (formData.asmUserCode ? { value: formData.asmUserCode, label: formData.asmUserCode } : null),
    [asmUserOptions, formData.asmUserCode, selectedAsmUserOption]
  );

  const resolvedDivisionOption = useMemo(
    () => divisionOptions.find((option) => option.value === formData.division)
      || selectedDivisionOption
      || (formData.division ? { value: formData.division, label: formData.division } : null),
    [divisionOptions, formData.division, selectedDivisionOption]
  );

  const resolvedFilterDivisionOption = useMemo(
    () => divisionOptions.find((option) => option.value === filters.division) || null,
    [divisionOptions, filters.division]
  );

  const resolvedFilterStatusOption = useMemo(
    () => statusFilterOptions.find((option) => option.value === filters.status) || null,
    [filters.status]
  );

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  useEffect(() => {
    const peopleForStay = toNumber(formData.expecteddealersno) + toNumber(formData.expectedagentsno) + toNumber(formData.expectedstaffno);
    const customerCount = toNumber(formData.expecteddealersno) + toNumber(formData.expectedretailersno);
    const hotelTotal = peopleForStay * toNumber(formData.hotelcostperperson);
    const travelTotal = peopleForStay * toNumber(formData.travelcostperperson);
    const totalEstimated = hotelTotal
      + travelTotal
      + toNumber(formData.displaycost)
      + toNumber(formData.logisticscost)
      + toNumber(formData.photovideoshootcost);
    const expectedSalesAmount = toNumber(formData.expectedsalesamount);

    const nextCalculatedValues = {
      hoteltotalcost: formatDecimal(hotelTotal),
      traveltotalcost: formatDecimal(travelTotal),
      totalestimatedcost: formatDecimal(totalEstimated),
      expenditurepercentage: expectedSalesAmount > 0 ? formatDecimal((totalEstimated / expectedSalesAmount) * 100) : '',
      expenditurepercustomer: customerCount > 0 ? formatDecimal(totalEstimated / customerCount) : ''
    };

    setFormData((prev) => {
      const hasChanges = Object.entries(nextCalculatedValues).some(([key, value]) => prev[key] !== value);
      return hasChanges ? { ...prev, ...nextCalculatedValues } : prev;
    });
  }, [
    formData.displaycost,
    formData.expectedagentsno,
    formData.expecteddealersno,
    formData.expectedretailersno,
    formData.expectedstaffno,
    formData.expectedsalesamount,
    formData.hotelcostperperson,
    formData.logisticscost,
    formData.photovideoshootcost,
    formData.travelcostperperson
  ]);

  const handleOpenCreateDialog = () => {
    setFormData(initialForm);
    setSelectedAsmUserOption(null);
    setSelectedDivisionOption(null);
    setAsmUserSearchText('');
    setErrors({});
    setShowCreateDialog(true);
  };

  const handleCloseCreateDialog = () => {
    if (isSubmitting) {
      return;
    }

    setShowCreateDialog(false);
    setSelectedAsmUserOption(null);
    setSelectedDivisionOption(null);
    setAsmUserSearchText('');
    setErrors({});
  };

  const validateForm = () => {
    const nextErrors = {};

    requiredFields.forEach((fieldName) => {
      if (!String(formData[fieldName] || '').trim()) {
        nextErrors[fieldName] = 'Required';
      }
    });

    numericFields.forEach((fieldName) => {
      const value = String(formData[fieldName] || '').trim();

      if (value && Number.isNaN(Number(value))) {
        nextErrors[fieldName] = 'Enter a valid number';
      }
    });

    if (formData.startdate && formData.enddate && new Date(formData.startdate) > new Date(formData.enddate)) {
      nextErrors.enddate = 'End date cannot be before start date';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    const nextValue = numericFields.includes(name) ? sanitizeNumericInput(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleAsmUserChange = (selectedOption) => {
    setSelectedAsmUserOption(selectedOption);
    setFormData((prev) => ({
      ...prev,
      asmUserCode: selectedOption?.value || ''
    }));
    setErrors((prev) => ({
      ...prev,
      asmUserCode: ''
    }));
  };

  const handleDivisionChange = (selectedOption) => {
    setSelectedDivisionOption(selectedOption);
    setFormData((prev) => ({
      ...prev,
      division: selectedOption?.value || ''
    }));
    setErrors((prev) => ({
      ...prev,
      division: ''
    }));
  };

  const handleFilterFieldChange = (event) => {
    const { name, value } = event.target;

    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterDivisionChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      division: selectedOption?.value || ''
    }));
  };

  const handleFilterStatusChange = (selectedOption) => {
    setFilters((prev) => ({
      ...prev,
      status: selectedOption?.value || ''
    }));
  };

  const handleResetFilters = () => {
    setFilters(initialDisplayAndEventsFilters);
  };

  const handleOpenActionHistory = (request) => {
    setSelectedActionHistoryEvent(request);
    loadActionHistory(request?.eventid);
  };

  const handleCloseActionHistory = () => {
    setSelectedActionHistoryEvent(null);
    clearActionHistory();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      showAlert('Please fill all mandatory fields correctly.');
      return;
    }

    try {
      const response = await submitEventRequest(formData);
      await showSuccessAlert(response?.msg || 'Request Submitted Successfully', 'Success');
      setShowCreateDialog(false);
      setFormData(initialForm);
      setSelectedAsmUserOption(null);
      setSelectedDivisionOption(null);
      setAsmUserSearchText('');
    } catch (error) {
      showAlert(error?.message || 'Failed to submit event request.');
    }
  };

  const renderField = (field) => (
    <Grid item xs={12} md={field.md || 4} key={field.name}>
      {field.name === 'asmUserCode' ? (
        <>
          <Box sx={searchableFieldWrapperSx}>
            <Typography variant="body2" sx={searchableFieldLabelSx}>
              {field.label} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Select
              value={resolvedAsmUserOption}
              placeholder={field.placeholder}
              isSearchable
              isClearable
              isLoading={isAsmUserOptionsLoading}
              options={asmUserOptions}
              styles={searchableDropdownStyles}
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition="fixed"
              noOptionsMessage={getSearchableNoOptionsMessage}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  setAsmUserSearchText(inputValue);
                }
                if (actionMeta.action === 'menu-close' && !inputValue) {
                  setAsmUserSearchText('');
                }
              }}
              onChange={handleAsmUserChange}
            />
          </Box>
          {errors.asmUserCode ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
              {errors.asmUserCode}
            </Typography>
          ) : null}
        </>
      ) : field.name === 'division' ? (
        <>
          <Box sx={searchableFieldWrapperSx}>
            <Typography variant="body2" sx={searchableFieldLabelSx}>
              {field.label} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <Select
              value={resolvedDivisionOption}
              placeholder={field.placeholder}
              isSearchable
              isClearable
              isLoading={isDivisionOptionsLoading}
              options={divisionOptions}
              styles={searchableDropdownStyles}
              menuPortalTarget={selectMenuPortalTarget}
              menuPosition="fixed"
              noOptionsMessage={() => 'No data found'}
              onChange={handleDivisionChange}
            />
          </Box>
          {errors.division ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75, ml: 1.75 }}>
              {errors.division}
            </Typography>
          ) : null}
        </>
      ) : (
        <TextField
          required={requiredFields.includes(field.name)}
          fullWidth
          label={field.label}
          name={field.name}
          type={field.type || 'text'}
          value={formData[field.name] || ''}
          onChange={handleFieldChange}
          error={Boolean(errors[field.name])}
          helperText={errors[field.name]}
          placeholder={field.placeholder || ''}
          inputProps={{
            inputMode: numericFields.includes(field.name) ? 'decimal' : undefined,
            readOnly: field.readOnly
          }}
          {...textFieldProps}
        />
      )}
    </Grid>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        width: '100%',
        minHeight: '100%',
        padding: '0 12px 28px'
      }}
    >
      {isSubmitting ? (
        <div className="overlay">
          <Loader />
          <span style={{ marginLeft: '10px' }}>Submitting event request...</span>
        </div>
      ) : null}

      <div className="cutrange" style={{ width: '100%', maxWidth: '100%' }}>
        <span className="font20">DISPLAY AND EVENTS</span>
      </div>

      <Card
        sx={{
          width: '100%',
          maxWidth: '100%',
          p: { xs: 1.5, md: 3, xl: 4 },
          borderRadius: 2.5,
          position: 'relative'
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography variant="h5">Display and Events List</Typography>
            {/* <Typography variant="body2" color="text.secondary">
              Total Rows: {totalRows}
            </Typography> */}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            size="small"
            sx={{
              textTransform: 'none',
              alignSelf: { xs: 'flex-end', sm: 'center' },
              width: 'fit-content',
              minWidth: 170,
              ml: { xs: 'auto', sm: 0 },
              whiteSpace: 'nowrap',
              px: 1.75,
              py: 0.75,
              flexShrink: 0
            }}
          >
            Create Event Request
          </Button>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Event Name"
              name="eventname"
              value={filters.eventname}
              onChange={handleFilterFieldChange}
              placeholder="Search event name"
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={searchableFieldWrapperSx}>
              <Typography variant="body2" sx={searchableFieldLabelSx}>
                Division
              </Typography>
              <Select
                value={resolvedFilterDivisionOption}
                placeholder="Select Division"
                isSearchable
                isClearable
                isLoading={isDivisionOptionsLoading}
                options={divisionOptions}
                styles={searchableDropdownStyles}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                noOptionsMessage={() => 'No data found'}
                onChange={handleFilterDivisionChange}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="fromDate"
              type="date"
              value={filters.fromDate}
              onChange={handleFilterFieldChange}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="toDate"
              type="date"
              value={filters.toDate}
              onChange={handleFilterFieldChange}
              {...textFieldProps}
            />
          </Grid>
          <Grid item xs={12} sm={8} md={2}>
            <Box sx={searchableFieldWrapperSx}>
              <Typography variant="body2" sx={searchableFieldLabelSx}>
                Status
              </Typography>
              <Select
                value={resolvedFilterStatusOption}
                placeholder="Select Status"
                isSearchable
                isClearable
                options={statusFilterOptions}
                styles={searchableDropdownStyles}
                menuPortalTarget={selectMenuPortalTarget}
                menuPosition="fixed"
                noOptionsMessage={() => 'No data found'}
                onChange={handleFilterStatusChange}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={4} md={12} lg={1}>
            <Button fullWidth variant="outlined" size="small" onClick={handleResetFilters} sx={{ minHeight: 40, textTransform: 'none' }}>
              Reset
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ position: 'relative' }}>
          {isTableLoading ? (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)'
              }}
            >
              <Loader />
            </Box>
          ) : null}
          <TableContainer
            sx={{
              width: '100%',
              overflowX: 'auto',
              border: '1px solid rgba(224, 224, 224, 0.8)',
              borderRadius: 2
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                width: '100%',
                tableLayout: 'auto',
                '& .MuiTableCell-root': {
                  borderBottomColor: 'rgba(224, 224, 224, 0.8)'
                }
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={compactHeaderCellSx}>Event No</TableCell>
                  <TableCell sx={compactHeaderCellSx}>Event Name</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Division</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Event Type</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Request Date</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Event Dates</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Expected Sales Amt</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Total Cost</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Status</TableCell>
                  <TableCell sx={{ ...compactHeaderCellSx, ...centeredCellSx }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventRequests.length > 0 ? (
                  eventRequests.map((request) => (
                    <TableRow key={request.eventid || request.createdat} hover>
                      <TableCell sx={compactBodyCellSx}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setSelectedEventRequest(request)}
                          sx={{
                            textTransform: 'none',
                            minWidth: 'auto',
                            p: 0,
                            justifyContent: 'flex-start',
                            color: '#1976d2',
                            textDecoration: 'underline',
                            fontWeight: 600
                          }}
                        >
                          {request.showid || '-'}
                        </Button>
                      </TableCell>
                      <TableCell sx={compactBodyCellSx}>{request.eventname || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{request.division || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{request.eventtype || '-'}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatDisplayDate(request.requestdate)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        {formatDisplayDate(request.startdate)} - {formatDisplayDate(request.enddate)}
                      </TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatAmount(request.expectedsalesamount)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>{formatAmount(request.totalestimatedcost)}</TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        <Box component="span" sx={{ fontSize: '0.72rem', ...getStatusTextSx(request.status) }}>
                          {request.status || '-'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ ...compactBodyCellSx, ...centeredCellSx }}>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenActionHistory(request)}
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid rgba(25, 118, 210, 0.35)',
                            color: '#1976d2',
                            '&:hover': {
                              borderColor: '#1976d2',
                              backgroundColor: 'rgba(25, 118, 210, 0.08)'
                            }
                          }}
                          title="History"
                        >
                          <HistoryOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 2, textAlign: 'center', color: 'text.secondary' }}>
                      No data found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Card>

      <Dialog open={showCreateDialog} onClose={handleCloseCreateDialog} fullWidth maxWidth="lg">
        <Box component="form" onSubmit={handleSubmit}>
          <DialogTitle>Create Event Request</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {formFields.map(renderField)}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseCreateDialog} sx={{ textTransform: 'none' }} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ textTransform: 'none' }} disabled={isSubmitting}>
              Submit Request
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(selectedEventRequest)} onClose={() => setSelectedEventRequest(null)} fullWidth maxWidth="lg">
        <DialogTitle>Event Request Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            {detailFields.map((field) => (
              <Grid item xs={12} md={field.md || 4} key={field.name}>
                <TextField
                  fullWidth
                  label={field.label}
                  value={
                    dateFields.includes(field.name)
                      ? formatDisplayDate(selectedEventRequest?.[field.name])
                      : moneyFields.includes(field.name)
                        ? formatAmount(selectedEventRequest?.[field.name])
                        : selectedEventRequest?.[field.name] || ''
                  }
                  InputProps={{ readOnly: true }}
                  {...textFieldProps}
                />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedEventRequest(null)} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedActionHistoryEvent)} onClose={handleCloseActionHistory} fullWidth maxWidth="lg">
        <DialogTitle>Action History</DialogTitle>
        <DialogContent>
          {isHistoryLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              Loading history...
            </Typography>
          ) : historyError ? (
            <Typography variant="body2" color="error" sx={{ pt: 1 }}>
              {historyError}
            </Typography>
          ) : actionHistory.length > 0 ? (
            <TableContainer sx={{ pt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Action By</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {actionHistory.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.action || '-'}</TableCell>
                      <TableCell>{item.actionBy || '-'}</TableCell>
                      <TableCell>{item.role || '-'}</TableCell>
                      <TableCell>
                        <Box component="span" sx={{ fontSize: '0.72rem', ...getStatusTextSx(item.status) }}>
                          {item.status || '-'}
                        </Box>
                      </TableCell>
                      {/* <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.remarks || '-'}</TableCell> */}
                      <TableCell>{formatHistoryDateTime(item.actionDate)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
              No history found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseActionHistory} sx={{ textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DispayAndEvents;
